import { can, isFinalLike, type ReportStatus } from '@quickreport/domain';
import { type LocalDb, queueChange, type Tx } from '@quickreport/sync';
import { recordAudit } from './audit';
import { WorkflowError, type WorkspaceCtx } from './reports';

/**
 * Deleting a report or a patient takes the work out of every list, total, link and reminder, and
 * keeps the audit trail. The rows are only marked deleted (and synced like any change), so the
 * LabView team can still bring them back if it was a mistake.
 */

export interface ReportDeletionImpact {
  name: string;
  labNo: string;
  patient: string;
  status: ReportStatus;
  /** Signed (final, partial or revoked): the patient may already hold a copy. */
  signed: boolean;
  /** The public link and QR code on printed copies stop working. */
  hasLink: boolean;
  payments: number;
  paidPaise: number;
  /** Other live reports in the same visit; when none, the visit goes with it. */
  siblings: number;
  /** Whether this person may delete it at all. */
  allowed: boolean;
  /** Plain reason when not allowed. */
  blockedReason: string | null;
}

export interface PatientDeletionImpact {
  name: string;
  visits: number;
  reports: number;
  signed: number;
  hasLink: boolean;
  payments: number;
  paidPaise: number;
  allowed: boolean;
  blockedReason: string | null;
}

const SIGNED_ONLY_OWNER =
  'Only the lab owner or a lab admin can delete a signed report. Ask them, or revoke the report instead.';
const PATIENT_SIGNED_ONLY_OWNER =
  'This patient has signed reports, so only the lab owner or a lab admin can delete them.';

export async function reportDeletionImpact(
  db: Pick<LocalDb, 'query'> | Tx,
  roles: readonly string[],
  reportId: string,
): Promise<ReportDeletionImpact | null> {
  const r = await db.query<{
    name: string;
    lab_no: string;
    status: ReportStatus;
    visit_id: string;
    verify_code: string | null;
    patient: string | null;
  }>(
    `select name, lab_no, status, visit_id, verify_code, content->'patient'->>'name' as patient
       from reports where id = $1 and deleted_at is null`,
    [reportId],
  );
  const row = r.rows[0];
  if (!row) return null;
  const pay = await db.query<{ n: number; paise: number }>(
    `select count(*)::int as n, coalesce(sum(amount_paise), 0)::float8 as paise
       from payments where report_id = $1 and deleted_at is null`,
    [reportId],
  );
  const sib = await db.query<{ n: number }>(
    `select count(*)::int as n from reports where visit_id = $1 and id <> $2 and deleted_at is null`,
    [row.visit_id, reportId],
  );
  const signed = isFinalLike(row.status);
  const allowed = !signed || can(roles, 'delete_signed');
  return {
    name: row.name,
    labNo: row.lab_no,
    patient: row.patient ?? '',
    status: row.status,
    signed,
    hasLink: Boolean(row.verify_code),
    payments: Number(pay.rows[0]?.n ?? 0),
    paidPaise: Number(pay.rows[0]?.paise ?? 0),
    siblings: Number(sib.rows[0]?.n ?? 0),
    allowed,
    blockedReason: allowed ? null : SIGNED_ONLY_OWNER,
  };
}

export async function patientDeletionImpact(
  db: Pick<LocalDb, 'query'> | Tx,
  roles: readonly string[],
  patientId: string,
): Promise<PatientDeletionImpact | null> {
  const p = await db.query<{ name: string }>(
    'select name from patients where id = $1 and deleted_at is null',
    [patientId],
  );
  if (!p.rows[0]) return null;
  const v = await db.query<{ n: number }>(
    'select count(*)::int as n from visits where patient_id = $1 and deleted_at is null',
    [patientId],
  );
  const r = await db.query<{ n: number; signed: number; linked: number }>(
    `select count(*)::int as n,
            count(*) filter (where r.status in ('final', 'partial', 'revoked'))::int as signed,
            count(*) filter (where r.verify_code is not null)::int as linked
       from reports r join visits v on v.id = r.visit_id
      where v.patient_id = $1 and r.deleted_at is null and v.deleted_at is null`,
    [patientId],
  );
  const pay = await db.query<{ n: number; paise: number }>(
    `select count(*)::int as n, coalesce(sum(p.amount_paise), 0)::float8 as paise
       from payments p join reports r on r.id = p.report_id join visits v on v.id = r.visit_id
      where v.patient_id = $1 and p.deleted_at is null and r.deleted_at is null`,
    [patientId],
  );
  const signed = Number(r.rows[0]?.signed ?? 0);
  const allowed = signed === 0 || can(roles, 'delete_signed');
  return {
    name: p.rows[0].name,
    visits: Number(v.rows[0]?.n ?? 0),
    reports: Number(r.rows[0]?.n ?? 0),
    signed,
    hasLink: Number(r.rows[0]?.linked ?? 0) > 0,
    payments: Number(pay.rows[0]?.n ?? 0),
    paidPaise: Number(pay.rows[0]?.paise ?? 0),
    allowed,
    blockedReason: allowed ? null : PATIENT_SIGNED_ONLY_OWNER,
  };
}

/**
 * Mark a report's payments deleted. People who handle the counter also send that change
 * themselves; for everyone else the server removes them when it receives the report's deletion.
 */
async function dropPayments(tx: Tx, ctx: WorkspaceCtx, reportIds: string[]): Promise<void> {
  if (reportIds.length === 0) return;
  const pays = await tx.query<{ id: string }>(
    'select id from payments where report_id = any($1::uuid[]) and deleted_at is null',
    [reportIds],
  );
  const send = can(ctx.roles, 'manage_billing');
  for (const p of pays.rows) {
    await tx.query('update payments set deleted_at = now(), updated_at = now() where id = $1', [
      p.id,
    ]);
    if (send) await queueChange(tx, 'payments', p.id);
  }
}

/** Delete one report, its payments, and its visit when it was the visit's last report. */
export async function deleteReport(
  db: LocalDb,
  ctx: WorkspaceCtx,
  reportId: string,
): Promise<void> {
  await db.transaction(async (tx) => {
    const impact = await reportDeletionImpact(tx, ctx.roles, reportId);
    if (!impact) throw new WorkflowError('This report was already deleted.');
    if (!impact.allowed) throw new WorkflowError(impact.blockedReason ?? SIGNED_ONLY_OWNER);
    const visit = await tx.query<{ visit_id: string }>(
      'select visit_id from reports where id = $1',
      [reportId],
    );
    const visitId = visit.rows[0]?.visit_id;

    await dropPayments(tx, ctx, [reportId]);
    await tx.query('update reports set deleted_at = now(), updated_at = now() where id = $1', [
      reportId,
    ]);
    await queueChange(tx, 'reports', reportId);
    if (visitId && impact.siblings === 0) {
      await tx.query('update visits set deleted_at = now(), updated_at = now() where id = $1', [
        visitId,
      ]);
      await queueChange(tx, 'visits', visitId);
    }
    await recordAudit(tx, ctx.orgId, ctx, {
      entity: 'report',
      entityId: reportId,
      action: 'deleted',
      before: {
        labNo: impact.labNo,
        name: impact.name,
        status: impact.status,
        paymentsRemoved: impact.payments,
        paidPaise: impact.paidPaise,
      },
    });
  });
}

/** Delete a patient with every visit, report and payment under them. */
export async function deletePatient(
  db: LocalDb,
  ctx: WorkspaceCtx,
  patientId: string,
): Promise<void> {
  await db.transaction(async (tx) => {
    const impact = await patientDeletionImpact(tx, ctx.roles, patientId);
    if (!impact) throw new WorkflowError('This patient was already deleted.');
    if (!impact.allowed) throw new WorkflowError(impact.blockedReason ?? PATIENT_SIGNED_ONLY_OWNER);

    const reports = await tx.query<{ id: string; lab_no: string; status: string }>(
      `select r.id, r.lab_no, r.status from reports r join visits v on v.id = r.visit_id
        where v.patient_id = $1 and r.deleted_at is null`,
      [patientId],
    );
    const visits = await tx.query<{ id: string }>(
      'select id from visits where patient_id = $1 and deleted_at is null',
      [patientId],
    );
    const ids = reports.rows.map((r) => r.id);

    // In the order the server needs them: payments, reports, visits, and last the patient.
    await dropPayments(tx, ctx, ids);
    for (const r of reports.rows) {
      await tx.query('update reports set deleted_at = now(), updated_at = now() where id = $1', [
        r.id,
      ]);
      await queueChange(tx, 'reports', r.id);
    }
    for (const v of visits.rows) {
      await tx.query('update visits set deleted_at = now(), updated_at = now() where id = $1', [
        v.id,
      ]);
      await queueChange(tx, 'visits', v.id);
    }
    await tx.query('update patients set deleted_at = now(), updated_at = now() where id = $1', [
      patientId,
    ]);
    await queueChange(tx, 'patients', patientId);
    await recordAudit(tx, ctx.orgId, ctx, {
      entity: 'patient',
      entityId: patientId,
      action: 'deleted',
      before: {
        visits: impact.visits,
        reports: impact.reports,
        signedReports: impact.signed,
        paymentsRemoved: impact.payments,
        paidPaise: impact.paidPaise,
        reportLabNos: reports.rows.map((r) => r.lab_no),
      },
    });
  });
}
